import { z } from 'zod';
import { ResourceOwnerInputSchema } from '@reflex/shared';

/** Attachment id the launch composer stores on the agent. */
export const AWS_CREDENTIAL_ATTACHMENT_ID = 'aws-credential';

/**
 * The kinds of AWS auth a credential can register.
 *
 * - `access_key`: a static IAM access key pair (plus optional session token),
 *   stored encrypted in the plugin's secret partition.
 * - `assume_role`: an IAM role the devbox assumes via STS, using another
 *   `access_key` credential in the same org as the `source_profile`.
 */
export const AwsCredentialKindSchema = z.enum(['access_key', 'assume_role']);
export type AwsCredentialKind = z.infer<typeof AwsCredentialKindSchema>;

export const AWS_CREDENTIAL_KIND_LABELS: Record<AwsCredentialKind, string> = {
  access_key: 'Access key',
  assume_role: 'Assume role',
};

/** `us-east-1`, `eu-central-1`, `ap-southeast-2`, `us-gov-west-1`, … */
export const AWS_REGION_PATTERN = /^[a-z]{2}(-[a-z]+)+-\d$/;
/** `arn:aws:iam::123456789012:role/Name` (and the `aws-cn` / `aws-us-gov` partitions). */
export const AWS_ROLE_ARN_PATTERN = /^arn:aws(-[a-z]+)*:iam::\d{12}:role\/[\w+=,.@/-]+$/;
export const AWS_ACCESS_KEY_ID_PATTERN = /^[A-Z0-9]{16,128}$/;

const RegionSchema = z
  .string()
  .regex(AWS_REGION_PATTERN, 'Enter a region such as us-east-1')
  .optional();

/**
 * API-safe AWS credential. Key material never leaves the server; clients
 * only receive whether it is configured plus a redacted access-key-id hint.
 */
export const AwsCredentialSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: AwsCredentialKindSchema,
  /** Default region written to the profile. */
  region: z.string().nullable(),
  /** `access_key` only: whether the encrypted key pair is stored. */
  hasAccessKey: z.boolean(),
  /** `access_key` only: redacted access key id (`AKIA…7QZ`) for recognition. */
  accessKeyIdHint: z.string().nullable(),
  /** `assume_role` only. */
  roleArn: z.string().nullable(),
  externalId: z.string().nullable(),
  /** `assume_role` only: the `access_key` credential used as `source_profile`. */
  sourceCredentialId: z.string().nullable(),
  /** Owning organization. NOT NULL in the DB; every read fences on it. */
  organizationId: z.string(),
  /**
   * Per-resource owner: a prefixed id (`usr_` / `tem_` / `org_` / `prj_`).
   * `null` is the host-tier sentinel (read-only for everyone).
   */
  ownerId: z.string().nullable(),
  createdAt: z.number(),
});
export type AwsCredential = z.infer<typeof AwsCredentialSchema>;

/**
 * Create payload. Flat rather than a discriminated union so the same shape
 * drives the HTML form, the REST body, and the MCP tool; `superRefine`
 * enforces the per-kind required fields.
 */
export const CreateAwsCredentialBaseSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  kind: AwsCredentialKindSchema,
  region: RegionSchema.or(z.literal('')),
  // access_key
  accessKeyId: z.string().optional(),
  secretAccessKey: z.string().optional(),
  sessionToken: z.string().optional(),
  // assume_role
  roleArn: z.string().optional(),
  externalId: z.string().optional(),
  sourceCredentialId: z.string().optional(),
  /**
   * Resource ownership for the new credential: a prefixed owner id.
   * Required at runtime — an omitted owner is a 400 naming the eligible
   * scopes. Routes validate membership via `assertCanWriteOwned`.
   */
  owner: ResourceOwnerInputSchema.optional().describe(
    'Owner of the new resource: a usr_/tem_/org_/prj_ id. Required — there is no default scope.',
  ),
});

type RefineCtx = { addIssue: (issue: { code: 'custom'; path: string[]; message: string }) => void };
type CreateAwsCredentialBase = z.infer<typeof CreateAwsCredentialBaseSchema>;

export const ACCESS_KEY_ID_MESSAGE =
  'Enter the access key id (16 or more uppercase letters and digits)';
export const ROLE_ARN_MESSAGE = 'Enter a role ARN such as arn:aws:iam::123456789012:role/Deploy';

/** Per-kind required fields, shared by the create schema and the edit form. */
export function refineAwsCredentialFields(
  value: CreateAwsCredentialBase,
  ctx: RefineCtx,
  options: { requireAccessKey: boolean },
): void {
  if (value.kind === 'access_key') {
    const hasId = Boolean(value.accessKeyId);
    const hasSecret = Boolean(value.secretAccessKey);
    // A session token only ever travels with a fresh key pair, so entering
    // one alone must demand the pair rather than silently change nothing.
    const hasToken = Boolean(value.sessionToken);
    if (options.requireAccessKey || hasId || hasSecret || hasToken) {
      if (!value.accessKeyId || !AWS_ACCESS_KEY_ID_PATTERN.test(value.accessKeyId)) {
        ctx.addIssue({ code: 'custom', path: ['accessKeyId'], message: ACCESS_KEY_ID_MESSAGE });
      }
      if (!value.secretAccessKey) {
        ctx.addIssue({
          code: 'custom',
          path: ['secretAccessKey'],
          message: options.requireAccessKey
            ? 'Secret access key is required'
            : 'Rotate the access key id and secret together',
        });
      }
    }
  } else {
    if (!value.roleArn || !AWS_ROLE_ARN_PATTERN.test(value.roleArn)) {
      ctx.addIssue({ code: 'custom', path: ['roleArn'], message: ROLE_ARN_MESSAGE });
    }
    if (!value.sourceCredentialId) {
      ctx.addIssue({
        code: 'custom',
        path: ['sourceCredentialId'],
        message: 'Choose the access key that assumes this role',
      });
    }
  }
}

export const CreateAwsCredentialSchema = CreateAwsCredentialBaseSchema.superRefine((value, ctx) =>
  refineAwsCredentialFields(value, ctx, { requireAccessKey: true }),
);
export type CreateAwsCredential = z.infer<typeof CreateAwsCredentialSchema>;

/**
 * Update payload. `kind` is immutable; everything else is optional. For an
 * `access_key` credential the id and secret rotate together — one without
 * the other is rejected.
 */
export const UpdateAwsCredentialSchema = z
  .object({
    name: z.string().min(1).optional(),
    region: RegionSchema.or(z.literal('')),
    accessKeyId: z.string().optional(),
    secretAccessKey: z.string().optional(),
    sessionToken: z.string().optional(),
    roleArn: z.string().regex(AWS_ROLE_ARN_PATTERN).optional(),
    externalId: z.string().optional(),
    sourceCredentialId: z.string().optional(),
    owner: ResourceOwnerInputSchema.optional(),
  })
  .superRefine((value, ctx) => {
    const hasId = Boolean(value.accessKeyId);
    const hasSecret = Boolean(value.secretAccessKey);
    const hasToken = Boolean(value.sessionToken);
    if (hasId !== hasSecret || (hasToken && !hasId)) {
      ctx.addIssue({
        code: 'custom',
        path: [hasId ? 'secretAccessKey' : 'accessKeyId'],
        message: 'Rotate the access key id and secret together',
      });
    }
    if (hasId && !AWS_ACCESS_KEY_ID_PATTERN.test(value.accessKeyId!)) {
      ctx.addIssue({ code: 'custom', path: ['accessKeyId'], message: ACCESS_KEY_ID_MESSAGE });
    }
  });
export type UpdateAwsCredential = z.infer<typeof UpdateAwsCredentialSchema>;

/**
 * Attachment config persisted on the agent. Several credentials can be put on
 * one devbox — each becomes a named profile in `~/.aws/config`; the first is
 * the default (`AWS_PROFILE`).
 */
export const AwsAttachmentConfigSchema = z.object({
  credentialIds: z.array(z.string()).min(1),
});
export type AwsAttachmentConfig = z.infer<typeof AwsAttachmentConfigSchema>;

export function awsCredentialIdsFromAttachmentValue(value: unknown): string[] {
  const parsed = AwsAttachmentConfigSchema.safeParse(value);
  return parsed.success ? parsed.data.credentialIds : [];
}

/** Redact an access key id to its first four and last three characters. */
export function accessKeyIdHint(accessKeyId: string): string {
  if (accessKeyId.length <= 7) return '…';
  return `${accessKeyId.slice(0, 4)}…${accessKeyId.slice(-3)}`;
}
